import IRNewsAPI from "./IRNewsAPI.js";

const d = document;

/**
 * IRニュースリスト生成クラス
 */
export default class IRNewsList {
  /** 設定 */
  config = null;

  /** テンプレート */
  template = `
<li>
<a class="news-summary#{fileClass}" target="_blank" rel="noopener" href="#{url}">
<time class="news-summary__date" datetime="#{pubDateUTC}">#{pubDate}</time>
<p class="news-summary__category icon-category#{iconClass}">#{doctypeLabel}</p>
<p class="news-summary__title">#{title}<span class="news-summary__info">#{filesize}</span></p>
</a>
</li>
  `;

  /** 空のテンプレート */
  templateEmpty = `
  <li class="--empty"></li>
  `;

  /** リスト要素 */
  $list = null;

  /** IRNewsAPI */
  api = null;

  /** コールバック関数名 */
  callbackId = '';

  /**
   * コンストラクタ
   * @param {Object} config 設定
   * @param {HTMLElement} $list リスト要素
   */
  constructor(config, $list) {
    this.config = config;
    this.$list = $list;
    this.api = new IRNewsAPI(config);
    this.api.on('load', this.onReceive.bind(this));
    this.load();
  }

  /**
   * @return {String} 開始日(YYYYMMDD)
   */
  get pdate() {
    return this.$list.getAttribute('data-ir-pdate') || '';
  }

  /**
   * @param {String} dateStr 開始日(YYYYMMDD)
   */
  set pdate(dateStr) {
    return this.$list.setAttribute('data-ir-pdate', dateStr);
  }

  /**
   * @return {String} 終了日(YYYYMMDD)
   */
  get fdate() {
    return this.$list.getAttribute('data-ir-fdate') || '';
  }

  /**
   * @param {String} dateStr 終了日(YYYYMMDD)
   */
  set fdate(dateStr) {
    return this.$list.setAttribute('data-ir-fdate', dateStr);
  }

  /**
   * @return {String} 種別プロパティ名
   */
  get doctype() {
    return this.$list.getAttribute('data-ir-doctype') || '';
  }

  /**
   * @param {String} doctypePropName 種別プロパティ名
   */
  set doctype(doctypePropName) {
    return this.$list.setAttribute('data-ir-doctype', doctypePropName);
  }

  /**
   * @return {Number} 表示数
   */
  get len() {
    return parseInt(this.$list.getAttribute('data-ir-len'), 10) || 0;
  }

  /**
   * @param {Number} len 表示数
   */
  set len(len) {
    return this.$list.setAttribute('data-ir-len', parseInt(len, 10));
  }

  /**
   * @return {URLSearchParams} リクエスト用パラメータ（callbackを除く）
   */
  get params() {
    return {
      doctype: this.doctype || 'all',
      pdate: this.pdate,
      fdate: this.fdate,
      len: this.len,
    };
  }

  /**
   * リストを空にする
   */
  clearList() {
    this.$list.innerHTML = '';
  }

  /**
   * データ取得
   */
  load() {
    // 要素を待機状態にする
    this.$list.classList.add('--progress');

    // 読み込み
    this.api.load(this.params);
  }

  /**
   * 日付データ文字列をDateに変換
   * @param {String} publishDate 日付データ
   * @return {Date} Date
   */
  toDate(publishDate) {
    const date = publishDate.split(' ')[0].split('/');
    const time = publishDate.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    return new Date(
      date[0],
      parseInt(date[1], 10) - 1,
      parseInt(date[2], 10),
      parseInt(time[1], 10),
      parseInt(time[2], 10),
      parseInt(time[3], 10),
    );
  }

  /**
   * 表示用日付データに変換(YYYY.MM.DD)
   * @param {Date} date 日付データ
   * @return {String} 表示用日付
   */
  toDateDisplay(date) {
    return [
      date.getFullYear(),
      ('' + (date.getMonth() + 1)).padStart(2, '0'),
      ('' + date.getDate()).padStart(2, '0')
    ].join('.');
  }

  /**
   * time用日付データに変換(YYYY-MM-DDThh:mm:ss+09:00)
   * @param {Date} date 日付データ
   * @return {String} time用日付
   */
  toDateUTC(date) {
    return [
      [
        date.getFullYear(),
        ('' + (date.getMonth() + 1)).padStart(2, '0'),
        ('' + date.getDate()).padStart(2, '0')
      ].join('-'),
      'T',
      [
        ('' + date.getHours()).padStart(2, '0'),
        ('' + date.getMinutes()).padStart(2, '0'),
        ('' + date.getSeconds()).padStart(2, '0'),
      ].join(':'),
      '+09:00'
    ].join('');
  }

  /**
   * 表示用ファイルサイズ
   * @param {Number} filesize ファイルサイズ
   * @return {String} 表示用ファイルサイズ
   */
  toFileSizeDisplay(filesize) {
    let filesizeDisplay = '';
    filesize = parseInt(filesize, 10);

    const paren = {
      ja: ['（', '）'],
      en: ['(', ')'],
    };

    // ファイルサイズの単位表記を割り出し
    if (!isNaN(filesize)) {
      let suffixes = ['B', 'KB', 'MB', 'GB', 'TB'];
      let index = 0;
      while (filesize >= 1000) {
        index++;
        filesize = parseInt(filesize / 1000, 10);
      }
      filesizeDisplay = paren[this.config.lang][0] + filesize + suffixes[index] + paren[this.config.lang][1];
    }

    return filesizeDisplay;
  }

  /**
   * テンプレート埋め込み用変数リストを取得
   * @param {Object} data JSONデータ
   * @return {Object} テンプレート埋め込み用変数リスト
   */
  getRendarProperties(data) {
    // 日付
    const pubDate = this.toDate(data.publishDate);

    // 表示用日付
    const pubDateStr = this.toDateDisplay(pubDate);

    // <time>用の日付
    const pubDateUTCStr = this.toDateUTC(pubDate);

    // ファイル情報
    const file = data.files?.length ? data.files[0] : null;
    let fileClass = '';
    let filesizeDisplay = '';

    if (file) {
      file.size = parseInt(file.size, 10) || 0;

      // ファイルサイズ
      if (file.type === 'PDF-GENERAL' && file.size > 0) {
        fileClass = ' --file-pdf';
        filesizeDisplay = this.toFileSizeDisplay(file.size);
      } else {
        fileClass = ' --file-page';
      }
    } else {
      fileClass = ' --file-page';
    }

    // 種類名
    const doctypeInfo = this.config.getDoctypeInfoByDisclosureCode(data.disclosureCode);
    const doctypeLabel = doctypeInfo.doctypeLabel;
    const iconClass = doctypeInfo.iconClass;

    // テンプレート埋め込み用変数リスト
    const props = {
      title: data.title,
      url: file.url,
      pubDateUTC: pubDateUTCStr,
      pubDate: pubDateStr,
      filesize: filesizeDisplay,
      fileClass: fileClass,
      doctypeLabel: doctypeLabel,
      iconClass: iconClass
    };

    return props;
  }

  /**
   * レンダリング
   * @param {Object} datas JSONデータ
   */
  rendar(datas) {
    this.clearList();

    if (datas.items?.length) {
      datas.items.forEach((data) => {
        this.$list.appendChild(this.rendarItem(data));
      });
    } else {
      // 該当する記事がなかった時
      this.$list.innerHTML = this.templateEmpty;
    }

    this.$list.classList.remove('--progress');
  }

  /**
   * 個別要素レンダリング
   * @param {Object} data データ
   */
  rendarItem(data) {
    // テンプレート埋め込み用変数リスト
    const props = this.getRendarProperties(data);

    // テンプレートにデータを反映
    const $temp = d.createElement('ul');
    $temp.innerHTML = this.template.replace(/#\{([^}]+)\}/g, (matched, property) => {
      return props[property]
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      ;
    });

    const $el = $temp.children[0];
    return $el;
  }

  /**
   * JSONPのコールバック
   * @param {Object} datas データ
   */
  onReceive(datas) {
    this.rendar(datas);
  }
}
